/**
 * Minimap panel (_docs/tilemap/07 TM5 polish backlog). A small overview of
 * every painted chunk across all visible layers, with the current camera's
 * viewport drawn as an outline and click-to-pan. Reads directly from the
 * store's in-memory `layerTiles` (not the load-time `chunk_index` snapshot)
 * so it stays accurate while painting.
 *
 * Redraws on store changes that matter (camera moves, document load/undo)
 * plus a cheap 1s poll to pick up live paint strokes — the imperative
 * TilemapSurface has its own precise dirty-render loop; this panel doesn't
 * need frame-perfect accuracy for a thumbnail-sized overview.
 */

import { useEffect, useRef } from 'react';
import { tilemapStore, orderedLayers } from '../store/tilemapStore';
import { CHUNK_SIZE } from '../model/constants';

const SIZE = 160; // CSS px, square
const PADDING_CHUNKS = 1;
const MAX_SCALE = 10; // px per world unit — caps zoom-in on tiny maps

export function Minimap({ mainCanvas }: { mainCanvas: HTMLCanvasElement | null }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const draw = () => {
            const state = tilemapStore.getState();
            const doc = state.doc;
            canvas.width = SIZE;
            canvas.height = SIZE;
            ctx.clearRect(0, 0, SIZE, SIZE);
            ctx.fillStyle = 'rgba(0,0,0,0.05)';
            ctx.fillRect(0, 0, SIZE, SIZE);
            if (!doc) return;

            const cellSize = doc.tilemap.cell_size;

            // Bounding box of painted chunks across every layer.
            let has = false;
            let minCx = 0, minCy = 0, maxCx = 0, maxCy = 0;
            for (const layer of doc.layers) {
                const tiles = state.layerTiles.get(layer.uuid);
                if (!tiles) continue;
                for (const chunk of tiles.allChunks()) {
                    if (!has) { minCx = maxCx = chunk.cx; minCy = maxCy = chunk.cy; has = true; }
                    else {
                        minCx = Math.min(minCx, chunk.cx); maxCx = Math.max(maxCx, chunk.cx);
                        minCy = Math.min(minCy, chunk.cy); maxCy = Math.max(maxCy, chunk.cy);
                    }
                }
            }
            if (!has) {
                minCx = maxCx = 0;
                minCy = maxCy = 0;
            }
            minCx -= PADDING_CHUNKS; minCy -= PADDING_CHUNKS;
            maxCx += PADDING_CHUNKS; maxCy += PADDING_CHUNKS;

            const worldW = (maxCx - minCx + 1) * CHUNK_SIZE * cellSize;
            const worldH = (maxCy - minCy + 1) * CHUNK_SIZE * cellSize;
            const scale = Math.min(SIZE / worldW, SIZE / worldH, MAX_SCALE);

            const originWorldX = minCx * CHUNK_SIZE * cellSize;
            const originWorldY = minCy * CHUNK_SIZE * cellSize;
            const offsetX = (SIZE - worldW * scale) / 2;
            const offsetY = (SIZE - worldH * scale) / 2;

            // world (y-up) -> minimap pixel (y-down): flip vertically.
            const toPx = (wx: number, wy: number) => ({
                x: offsetX + (wx - originWorldX) * scale,
                y: SIZE - offsetY - (wy - originWorldY) * scale,
            });

            const chunkPx = CHUNK_SIZE * cellSize * scale;
            const hues = ['#4c6ef5', '#f5793b', '#37b24d', '#e64980', '#f59f00'];
            orderedLayers(doc).forEach((layer, i) => {
                if (!layer.visible) return;
                const tiles = state.layerTiles.get(layer.uuid);
                if (!tiles) return;
                ctx.fillStyle = hues[i % hues.length];
                ctx.globalAlpha = 0.85;
                for (const chunk of tiles.allChunks()) {
                    if (chunk.usedCount === 0) continue;
                    const p = toPx(chunk.cx * CHUNK_SIZE * cellSize, (chunk.cy + 1) * CHUNK_SIZE * cellSize);
                    ctx.fillRect(Math.round(p.x), Math.round(p.y), Math.ceil(chunkPx) + 1, Math.ceil(chunkPx) + 1);
                }
            });
            ctx.globalAlpha = 1;

            // Viewport rect, from the main canvas's actual CSS size.
            if (mainCanvas) {
                const rect = mainCanvas.getBoundingClientRect();
                const cam = state.camera;
                const p0 = toPx(cam.x, cam.y);
                const p1 = toPx(cam.x + rect.width / cam.zoom, cam.y + rect.height / cam.zoom);
                ctx.strokeStyle = '#F5793B';
                ctx.lineWidth = 1.5;
                ctx.strokeRect(
                    Math.min(p0.x, p1.x), Math.min(p0.y, p1.y),
                    Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y),
                );
            }
        };

        draw();
        const unsub = tilemapStore.subscribe(draw);
        const poll = setInterval(draw, 1000); // catches live painting between renderTick bumps
        return () => { unsub(); clearInterval(poll); };
    }, [mainCanvas]);

    const onClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const state = tilemapStore.getState();
        if (!state.doc || !mainCanvas) return;
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        const cellSize = state.doc.tilemap.cell_size;

        // Recompute the same bounds/scale used to draw, to invert the click.
        let has = false;
        let minCx = 0, minCy = 0, maxCx = 0, maxCy = 0;
        for (const layer of state.doc.layers) {
            const tiles = state.layerTiles.get(layer.uuid);
            if (!tiles) continue;
            for (const chunk of tiles.allChunks()) {
                if (!has) { minCx = maxCx = chunk.cx; minCy = maxCy = chunk.cy; has = true; }
                else {
                    minCx = Math.min(minCx, chunk.cx); maxCx = Math.max(maxCx, chunk.cx);
                    minCy = Math.min(minCy, chunk.cy); maxCy = Math.max(maxCy, chunk.cy);
                }
            }
        }
        if (!has) { minCx = maxCx = 0; minCy = maxCy = 0; }
        minCx -= PADDING_CHUNKS; minCy -= PADDING_CHUNKS; maxCx += PADDING_CHUNKS; maxCy += PADDING_CHUNKS;
        const worldW = (maxCx - minCx + 1) * CHUNK_SIZE * cellSize;
        const worldH = (maxCy - minCy + 1) * CHUNK_SIZE * cellSize;
        const scale = Math.min(SIZE / worldW, SIZE / worldH, MAX_SCALE);
        const originWorldX = minCx * CHUNK_SIZE * cellSize;
        const originWorldY = minCy * CHUNK_SIZE * cellSize;
        const offsetX = (SIZE - worldW * scale) / 2;
        const offsetY = (SIZE - worldH * scale) / 2;

        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const worldX = originWorldX + (px - offsetX) / scale;
        const worldY = originWorldY + (SIZE - offsetY - py) / scale;

        const mainRect = mainCanvas.getBoundingClientRect();
        const cam = state.camera;
        state.setCamera({
            ...cam,
            x: worldX - mainRect.width / cam.zoom / 2,
            y: worldY - mainRect.height / cam.zoom / 2,
        });
    };

    return (
        <div className="p-2">
            <div className="small text-secondary mb-1">Minimap</div>
            <canvas
                ref={canvasRef}
                width={SIZE}
                height={SIZE}
                style={{ width: SIZE, height: SIZE, cursor: 'pointer', border: '1px solid var(--tblr-border-color)' }}
                onClick={onClick}
            />
        </div>
    );
}
